import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { activityApi } from '../services/api';

type Ev = { page: string; action: string; target?: string; durationMs?: number; screen?: string };

const queue: Ev[] = [];
let timer: number | undefined;

function screenOf() {
  try { return `${window.screen.width}x${window.screen.height}`; } catch { return ''; }
}

function flush() {
  if (!queue.length || !localStorage.getItem('token')) return;
  const batch = queue.splice(0, 20);
  activityApi.record(batch).catch(() => undefined);
}

function push(ev: Ev) {
  queue.push({ ...ev, screen: screenOf() });
  if (queue.length >= 15) flush();
  else {
    window.clearTimeout(timer);
    timer = window.setTimeout(flush, 8000);
  }
}

function labelOf(el: Element) {
  const node = el.closest('a, button, [role="button"]');
  if (!node) return '';
  if (node.closest('input, textarea, select')) return '';
  const text = (node.getAttribute('aria-label') || node.getAttribute('data-track') || node.textContent || '')
    .replace(/\s+/g, ' ').trim();
  return text.slice(0, 80);
}

export default function ActivityTracker() {
  const location = useLocation();
  const prev = useRef<{ page: string; at: number } | null>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const el = event.target as Element | null;
      if (!el || !el.closest) return;
      const target = labelOf(el);
      if (!target) return;
      push({ page: window.location.pathname, action: 'click', target });
    };
    document.addEventListener('click', onClick, true);
    const beat = window.setInterval(flush, 8000);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.clearInterval(beat);
    };
  }, []);

  useEffect(() => {
    const page = location.pathname;
    if (page.startsWith('/login') || page.startsWith('/b/') || page.startsWith('/book/') || page.startsWith('/reset-password')) {
      prev.current = null;
      return;
    }
    const now = Date.now();
    const durationMs = prev.current ? now - prev.current.at : undefined;
    push({ page, action: 'page_view', durationMs });
    prev.current = { page, at: now };
  }, [location.pathname]);

  return null;
}
